import { useEffect, useState } from "react";
import { Printer } from "lucide-react";

import { API_BASE, apiFetch, loadStoredToken } from "@/api/client";
import type { Uuid } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Field, Select } from "@/components/Input";
import { LoadingState } from "@/components/States";

interface Template {
  key: string;
  label: string;
  width_mm: number;
  height_mm: number;
}

interface Subject {
  entity: string;
  entity_id: Uuid;
  display_code: string;
  name: string;
  subtitle: string;
  payload: string;
}

/**
 * Print a label for a place or a container.
 *
 * The label carries the display code and a QR pointing at the entity's UUID —
 * never at its name, so renaming the bin does not invalidate the sticker already
 * on it. Printing goes through the browser: an SVG sized in real millimetres
 * prints at the right size on whatever label stock the shop actually owns.
 */
export function LabelDialog({
  open,
  onOpenChange,
  entity,
  entityId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: "location" | "container" | "asset";
  entityId: Uuid;
}) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [template, setTemplate] = useState("standard");
  const [subject, setSubject] = useState<Subject | null>(null);
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void apiFetch<Template[]>("/labels/templates").then(setTemplates).catch(() => {});
    void apiFetch<Subject>(`/labels/${entity}/${entityId}`).then(setSubject).catch(() => {});
  }, [open, entity, entityId]);

  useEffect(() => {
    if (!open) return;
    const token = loadStoredToken();
    const controller = new AbortController();
    // Fetched rather than used as an <img src>: labels sit behind authentication
    // and an <img> carries no bearer token.
    fetch(`${API_BASE}/labels/${entity}/${entityId}/label.svg?template=${template}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.text() : Promise.reject(response)))
      .then(setSvg)
      .catch(() => {});
    return () => controller.abort();
  }, [open, entity, entityId, template]);

  function print() {
    if (!svg) return;
    // A dedicated window so the print dialog gets the label alone, at its real
    // size, without the application's layout around it.
    const win = window.open("", "_blank", "width=420,height=320");
    if (!win) return;
    win.document.write(
      `<!doctype html><title>${subject?.display_code ?? "Étiquette"}</title>` +
        `<style>@page{margin:0}body{margin:0;display:flex;justify-content:center}</style>` +
        svg,
    );
    win.document.close();
    win.focus();
    win.print();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Étiquette"
      description="Le QR pointe sur l'identité de l'entité, pas sur son nom — renommer ne casse pas l'étiquette."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant="primary" onClick={print} disabled={!svg}>
            <Printer className="size-4" aria-hidden />
            Imprimer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Format d'étiquette" htmlFor="label-template">
          <Select
            id="label-template"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
          >
            {templates.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </Select>
        </Field>

        {subject && (
          <dl className="grid grid-cols-2 gap-2 rounded-md border border-border bg-surface-sunken p-3 text-[12px]">
            <div>
              <dt className="text-ink-subtle">Code</dt>
              <dd className="font-mono text-ink">{subject.display_code}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-ink-subtle">Nom</dt>
              <dd className="truncate text-ink">{subject.name}</dd>
            </div>
            <div className="col-span-2 min-w-0">
              <dt className="text-ink-subtle">Le QR ouvre</dt>
              <dd className="truncate font-mono text-[11px] text-ink-muted">{subject.payload}</dd>
            </div>
          </dl>
        )}

        <div className="flex items-center justify-center rounded-md border border-border bg-white p-3">
          {svg ? (
            // The rendered label, at the size it will print. The SVG comes from
            // our own API and every customer-supplied string in it is escaped
            // server-side before it is written.
            <div className="max-w-full" dangerouslySetInnerHTML={{ __html: svg }} />
          ) : (
            <LoadingState label="Rendu de l'étiquette…" />
          )}
        </div>
      </div>
    </Dialog>
  );
}
